<template>
  <section v-if="items.length || error" class="pending-feedback" dir="rtl" aria-labelledby="pending-feedback-title">
    <div class="pending-feedback__header">
      <div>
        <h2 id="pending-feedback-title" class="pending-feedback__heading">بازخوردهای در انتظار شما</h2>
        <p class="pending-feedback__intro">بازخورد هر جلسه را از همین‌جا تکمیل کنید.</p>
      </div>
      <span v-if="items.length" class="pending-feedback__count">{{ number(items.length) }} بازخورد</span>
    </div>
    <v-alert v-if="error" type="info" text>{{ error }}</v-alert>
    <v-btn v-if="error" text :loading="loading" @click="load">تلاش دوباره</v-btn>
    <ul v-else class="pending-feedback__list">
      <li v-for="item in items" :key="item.uuid" class="pending-feedback__item">
        <div class="pending-feedback__details">
          <p class="pending-feedback__session">
            <template v-if="scheduled(item)">جلسه {{ scheduled(item).date }} <span class="pending-feedback__time">ساعت {{ scheduled(item).time }}</span></template>
            <template v-else>بازخورد جلسه</template>
          </p>
          <p v-if="item.therapist_name" class="pending-feedback__therapist">درمانگر: {{ item.therapist_name }}</p>
          <p class="pending-feedback__stage">{{ item.status === 'todo' ? 'ارزیابی پیش از جلسه (ORS)' : 'بازخورد جلسه (SRS)' }}</p>
        </div>
        <a :href="path(item.uuid)" class="pending-feedback__link" rel="noreferrer">تکمیل بازخورد <span aria-hidden="true">←</span></a>
      </li>
    </ul>
  </section>
</template>

<script>
import { feedbackPath, validFeedbackUuid } from '@/services/therapyFeedback'
import { feedbackNumber, feedbackSessionTime } from '@/services/therapyFeedbackDisplay'

export default {
  name: 'PendingTherapyFeedback',
  data: () => ({ items: [], error: '', loading: false, requestId: 0 }),
  computed: {
    timezone() { return (this.$store.getters.getProfile || {}).tz || 'UTC' }
  },
  mounted() {
    this.load()
    window.addEventListener('focus', this.refresh)
    window.addEventListener('pageshow', this.refresh)
    document.addEventListener('visibilitychange', this.refresh)
  },
  beforeDestroy() {
    this.requestId++
    window.removeEventListener('focus', this.refresh)
    window.removeEventListener('pageshow', this.refresh)
    document.removeEventListener('visibilitychange', this.refresh)
  },
  methods: {
    number: feedbackNumber,
    scheduled(item) { return feedbackSessionTime(item.scheduled_at, this.timezone) },
    path(uuid) { return feedbackPath(uuid, this.$route.params.lang) },
    refresh() { if (document.visibilityState !== 'hidden') this.load() },
    async load() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const response = await this.requesting('user', 'getPendingTherapyFeedback')
        if (requestId === this.requestId) this.items = (response.data.items || []).filter(item => validFeedbackUuid(item.uuid) && ['todo', 'ors_filled', 'ors_skipped'].includes(item.status))
      } catch (error) { if (requestId === this.requestId) this.error = 'دریافت بازخوردهای در انتظار ممکن نشد.' }
      finally { if (requestId === this.requestId) this.loading = false }
    }
  }
}
</script>

<style scoped>
.pending-feedback {
  margin-bottom: 24px;
  padding: 24px;
  border: 1px solid #dce9e4;
  border-radius: 16px;
  background: #fff;
  text-align: right;
  color: #293b42;
}
.pending-feedback .pending-feedback__heading,
.pending-feedback .pending-feedback__session {
  font-family: 'YekanBakh', main, sans-serif !important;
  font-weight: 700;
}
.pending-feedback__heading { font-size: 19px; line-height: 1.8; margin: 0; }
.pending-feedback__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.pending-feedback__intro { margin: 5px 0 0; color: #687b81; font-size: 14px; line-height: 1.9; }
.pending-feedback__count { white-space: nowrap; background: #edf8f3; color: #217a58; border-radius: 20px; padding: 5px 12px; font-size: 13px; }
.pending-feedback__list { list-style: none; margin: 16px 0 0; padding: 0 !important; }
.pending-feedback__item { display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 18px 0 0; border-top: 1px solid #edf1ef; }
.pending-feedback__item + .pending-feedback__item { margin-top: 18px; }
.pending-feedback__details { min-width: 0; }
.pending-feedback__session { font-size: 16px; line-height: 1.9; margin: 0; }
.pending-feedback__time { display: inline-block; margin-inline-start: 8px; font-weight: 400; }
.pending-feedback__therapist, .pending-feedback__stage { margin: 4px 0 0; color: #687b81; font-size: 14px; line-height: 1.8; }
.pending-feedback .pending-feedback__link { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; gap: 12px; min-height: 44px; padding: 9px 16px; border-radius: 10px; background: #edf8f3; color: #206e50; text-decoration: none; font-size: 14px; font-weight: 500; }
.pending-feedback__link:hover { background: #def2e8; }
.pending-feedback__link:focus-visible { outline: 2px solid #217a58; outline-offset: 3px; }
@media (max-width: 600px) {
  .pending-feedback { padding: 18px 16px; }
  .pending-feedback__heading { font-size: 17px; }
  .pending-feedback__header { gap: 8px; }
  .pending-feedback__count { padding: 4px 8px; font-size: 12px; }
  .pending-feedback__item { flex-direction: column; align-items: stretch; gap: 14px; }
  .pending-feedback__session { font-size: 15px; }
  .pending-feedback__link { align-self: flex-start; }
}
</style>
